// §318(a)(1) family ownership attribution for more-than-5% owner status.
//
// An individual is treated as owning stock owned by their spouse, children,
// grandchildren, and parents. Consequences for a plan census: the spouse, a
// child, a parent, or a grandparent of a more-than-5% owner is a deemed
// more-than-5% owner — and therefore an HCE (§414(q)) and a key employee
// (§416(i)) — regardless of pay. A GRANDCHILD of the owner is NOT attributed
// the owner's stock (the attribution runs owner→grandchild's stock, not the
// reverse), and siblings never attribute.
//
// §318(a)(5)(B) bars double family attribution: stock attributed from one
// family member is not re-attributed to another. So only DIRECT owners (an
// explicit Y flag or ownership_percent > 5) are attribution sources here —
// `family_of` should reference the actual owner, not another relative.

const normalizeRef = (value) => String(value ?? '').trim().toLowerCase()

export function isDirectFivePercentOwner(employee) {
  if (String(employee?.five_percent_owner || '').trim().toUpperCase() === 'Y') return true
  const percent = parseFloat(employee?.ownership_percent)
  return Number.isFinite(percent) && percent > 5
}

// Returns a new employee list where each employee whose `family_of` references
// a direct more-than-5% owner (by id or name, case-insensitive) carries
// `derived_five_percent_owner: true` and `attributed_ownership_from`. A
// `family_of` that matches no direct owner is flagged
// `family_attribution_unmatched` so the calculation can raise a review warning
// instead of silently ignoring the census entry.
export function applyFamilyAttribution(employees = []) {
  const list = Array.isArray(employees) ? employees : []
  if (!list.some((employee) => normalizeRef(employee?.family_of))) return list
  const byId = new Map()
  const byName = new Map()
  for (const employee of list) {
    if (!isDirectFivePercentOwner(employee)) continue
    const idRef = normalizeRef(employee.id)
    const nameRef = normalizeRef(employee.name)
    if (idRef) byId.set(idRef, employee)
    if (nameRef) byName.set(nameRef, employee)
  }
  return list.map((employee) => {
    const ref = normalizeRef(employee?.family_of)
    if (!ref || isDirectFivePercentOwner(employee)) return employee
    const owner = byId.get(ref) || byName.get(ref)
    if (!owner || owner === employee) return { ...employee, family_attribution_unmatched: true }
    return {
      ...employee,
      derived_five_percent_owner: true,
      attributed_ownership_from: String(owner.name || owner.id),
    }
  })
}
